import React, { useCallback, useEffect, useState } from "react";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { getSecurityEvents } from "../../lib/api";
import { PageLoader } from "../../components/ui/spinner";
import { Button } from "../../components/ui/button";

const PAGE_SIZE = 10;

const SEVERITY_TABS = [
  { key: "", label: "All" },
  { key: "critical", label: "Critical" },
  { key: "warning", label: "Warning" },
  { key: "info", label: "Info" },
];

// Graded on purpose. An expired device token is routine; a token issued for one
// device being used by another is not. A flat list buries the second among the first.
const SEVERITY_STYLE = {
  critical: "border border-red-300 text-red-700 bg-red-50",
  warning:  "border border-amber-300 text-amber-700 bg-amber-50",
  info:     "border border-slate-300 text-slate-500 bg-slate-50",
};

// What the empty table says. Split out of the JSX so the three cases read as
// three cases rather than a nested ternary (S3358).
function emptyStateMessage(loadError, hasFiltersApplied) {
  if (loadError) return <span className="text-red-500">{loadError}</span>;
  if (hasFiltersApplied) return "No records match.";
  // An empty table with no filters is the healthy case here, so it must not
  // read like a failed lookup.
  return "No unusual activity recorded.";
}

const TABLE_HEADERS = [
  "TIMESTAMP UTC",
  "SEVERITY",
  "EVENT TYPE",
  "ACTOR",
  "SOURCE IP",
  "ENDPOINT",
  "REASON",
];

function formatTs(ts) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return { date: "-", time: "-" };
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return {
    date: `${lookup.year}-${lookup.month}-${lookup.day}`,
    time: `${lookup.hour}:${lookup.minute}`,
  };
}

function getTodayDateString() {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
}

/**
 * Unusual Activity — requests the system refused.
 *
 * Reads Security-Events, which holds only refusals: denied approvals, identity
 * mismatches, rejected tokens, failed sign-ins. Routine activity lives in
 * Activity-Trace and is shown on the Activity Trace page instead, so this list
 * stays short enough to actually read.
 */
export default function UnusualActivityPage() {
  const todayDate = getTodayDateString();
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [severity, setSeverity] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, totalRecords: 0, totalPages: 1 });

  // Debounced so typing doesn't fire a request per keystroke at a lambda that
  // scans the table on every call.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchPage = useCallback((page = 1) => {
    setLoading(true);
    setLoadError(null);
    getSecurityEvents({ page, limit: PAGE_SIZE, fromDate, toDate, severity, search: debouncedSearch })
      .then(({ records: recs, pagination: pageInfo }) => {
        setRecords(recs);
        setPagination({
          page:         pageInfo.page         ?? page,
          limit:        pageInfo.limit        ?? PAGE_SIZE,
          totalRecords: pageInfo.totalRecords ?? recs.length,
          totalPages:   pageInfo.totalPages   ?? 1,
        });
      })
      .catch((err) => {
        setRecords([]);
        setLoadError(err.message || "Failed to load unusual activity.");
        setPagination({ page: 1, limit: PAGE_SIZE, totalRecords: 0, totalPages: 1 });
      })
      .finally(() => setLoading(false));
  }, [fromDate, toDate, severity, debouncedSearch]);

  // Every filter — severity, dates and search — is applied server-side before
  // pagination, so the counts describe the filtered set and a match on a later
  // page is still found. Always refetch from page 1: the previous page number is
  // meaningless against a different result set.
  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  // Already filtered, sorted and paginated by the lambda — rendered as returned.
  const pageRecords = records;
  const isInitialLoading = loading && records.length === 0;
  const hasFiltersApplied = debouncedSearch || fromDate || toDate || severity;

  if (isInitialLoading) return <PageLoader />;

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Unusual Activity</h1>
          <p className="text-sm text-slate-500 mt-1">
            Requests the system refused — denied permissions, identity mismatches, rejected tokens.
          </p>
        </div>
      </div>

      {/* Main card */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {/* Search + severity filter + dates */}
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              className="pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#08148C]/20 w-56"
              placeholder="Actor, IP, event type..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {SEVERITY_TABS.map((t) => (
              <button
                key={t.key || "all"}
                onClick={() => setSeverity(t.key)}
                className={`text-xs font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${
                  severity === t.key
                    ? "bg-[#08148C] text-white border-[#08148C]"
                    : "bg-white text-slate-600 border-slate-300 hover:border-[#08148C] hover:text-[#08148C]"
                }`}
              >
                {t.label}
              </button>
            ))}
            <span className="ml-auto text-xs text-slate-400 whitespace-nowrap font-bold">
              {pagination.totalRecords.toLocaleString()} events
              {hasFiltersApplied && <span className="ml-2 text-slate-500">(Filtered)</span>}
            </span>
            <div className="ml-8 flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600 whitespace-nowrap" htmlFor="unusual-from-date">
                From
              </label>
              <input
                id="unusual-from-date"
                type="date"
                className="px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#08148C]/20"
                value={fromDate}
                max={todayDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600 whitespace-nowrap" htmlFor="unusual-to-date">
                To
              </label>
              <input
                id="unusual-to-date"
                type="date"
                className="px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#08148C]/20"
                value={toDate}
                max={todayDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100">
                {TABLE_HEADERS.map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading && (
                <tr>
                  <td colSpan={TABLE_HEADERS.length} className="py-12">
                    <PageLoader />
                  </td>
                </tr>
              )}
              {!loading && pageRecords.length === 0 && (
                <tr>
                  <td colSpan={TABLE_HEADERS.length} className="text-center text-slate-400 py-12">
                    {emptyStateMessage(loadError, hasFiltersApplied)}
                  </td>
                </tr>
              )}
              {!loading && pageRecords.map((r) => {
                const { date, time } = formatTs(r.ts);
                const sev = String(r.severity || "warning").toLowerCase();
                return (
                  <tr key={r.eventId} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-2 text-slate-500 whitespace-nowrap">
                      <span className="block">{date}</span>
                      <span className="block text-xs">{time}</span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${SEVERITY_STYLE[sev] || SEVERITY_STYLE.warning}`}>
                        {sev.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-5 py-4 font-mono text-xs text-slate-700 whitespace-nowrap">{r.eventType || "—"}</td>
                    <td className="px-5 py-4 font-bold text-slate-800 whitespace-nowrap">{r.actor || "—"}</td>
                    <td className="px-5 py-4 font-mono text-xs text-slate-700 whitespace-nowrap">{r.sourceIp || "—"}</td>
                    <td className="px-5 py-4 font-mono text-xs text-slate-500">{r.endpoint || "—"}</td>
                    <td className="px-5 py-4 text-slate-600">{r.reason || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {records.length > 0 && pagination.totalPages > 1 && (
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={loading || pagination.page <= 1}
              onClick={() => fetchPage(pagination.page - 1)}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Prev
            </Button>
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => {
              const current = pagination.page;
              const total = pagination.totalPages;
              const show = p === 1 || p === total || Math.abs(p - current) <= 1;
              const ellipsisBefore = p === current - 2 && current - 2 > 1;
              const ellipsisAfter = p === current + 2 && current + 2 < total;
              if (ellipsisBefore || ellipsisAfter) {
                return <span key={p} className="px-1 text-sm text-slate-400">...</span>;
              }
              if (!show) return null;
              return (
                <Button
                  key={p}
                  type="button"
                  variant={p === current ? "default" : "outline"}
                  size="sm"
                  disabled={loading}
                  onClick={() => fetchPage(p)}
                  className="min-w-9"
                >
                  {p}
                </Button>
              );
            })}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={loading || pagination.page >= pagination.totalPages}
              onClick={() => fetchPage(pagination.page + 1)}
            >
              Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
